import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ChevronDown, ChevronRight, Save, RotateCcw, Sparkles, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import CandidateCard from "@/components/CandidateCard";
import InfoTip from "@/components/InfoTip";

const SENIORITY_OPTIONS = ["C-Suite","VP","Senior Director","Director","Associate Director","Senior Manager","Manager","Principal/Staff IC","Senior IC","IC"];
const FA_OPTIONS = ["Medical Affairs","Clinical Development","Regulatory Affairs","Drug Safety/PV","Commercial/Marketing","Research & Discovery","Manufacturing/CMC","Market Access","Medical Science Liaison","Biometrics/Data Science","Human Resources","Finance/Legal","Information Technology","Other"];
const TA_OPTIONS = ["Oncology","Rare Disease","Neurology","Immunology","Cardiovascular","Infectious Disease","Metabolic/Endocrine","Dermatology","Respiratory","Ophthalmology"];
const DEGREE_OPTIONS = ["PhD","MD","PharmD","MBA","MS/MSc","BS/BA"];
const GEO_OPTIONS = ["Northeast US","Mid-Atlantic US","Southeast US","Midwest US","Southwest US","West Coast US","Mountain/Plains US","Remote/Flexible"];
const EMPLOYER_OPTIONS = ["Large Pharma","Mid-size Pharma","Large Biotech","Small Biotech","CRO/CMO","Academic","Other"];
const COMPANY_TYPE_OPTIONS = ["Large Pharma","Mid-size Pharma","Biotech","Startup","CRO/CMO","Academic"];
const WORK_ENV_OPTIONS = ["Remote","Hybrid","On-site"];

function ChipGroup({ options, selected, onChange, testId }) {
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => toggle(opt)}
          className={`px-3 py-1 text-xs font-accent rounded-full border transition-colors ${
            selected.includes(opt) ? "bg-evergreen-950 text-white border-evergreen-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}>
          {opt}
        </button>
      ))}
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, tip, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 pb-4 mb-4">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          {title}
          {tip && <span onClick={e => e.stopPropagation()}><InfoTip text={tip} side="right" /></span>}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && children}
    </div>
  );
}

export default function TalentMarketExplorer() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [searchType, setSearchType] = useState("active_recruit");
  const [criteria, setCriteria] = useState({ seniority_levels: [], functional_areas: [], therapeutic_areas: [], degrees: [], geography_regions: [], employer_categories: [], remote_willing: false, intent_company_types: [], intent_work_environments: [], role_scope_notes: "" });
  const [jdText, setJdText] = useState("");
  const [poolCount, setPoolCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [analysisState, setAnalysisState] = useState("idle"); // idle | loading | done
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisMessage, setAnalysisMessage] = useState("");
  const [results, setResults] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [searchId, setSearchId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [jdParsing, setJdParsing] = useState(false);
  const [minScore, setMinScore] = useState(0);
  const [countError, setCountError] = useState(false);
  const [criteriaChanged, setCriteriaChanged] = useState(false);
  const countTimer = useRef(null);
  const lastAnalyzedCriteria = useRef(null);

  // Load existing search
  useEffect(() => {
    if (id) {
      api.get(`/role-searches/${id}`).then(r => {
        const s = r.data;
        setTitle(s.title);
        setSearchType(s.type);
        setCriteria({ seniority_levels: [], functional_areas: [], therapeutic_areas: [], degrees: [], geography_regions: [], employer_categories: [], remote_willing: false, intent_company_types: [], intent_work_environments: [], role_scope_notes: "", ...s.criteria });
        setJdText(s.role_description_text || "");
        setSearchId(s.id);
        if (s.last_deep_analysis_run_at) {
          api.get(`/deep-analysis/${s.id}/results`).then(r2 => {
            if (r2.data.results?.length) { setResults(r2.data.results); setAnalysisState("done"); }
          }).catch(() => {});
        }
      }).catch(() => toast.error("Failed to load search"));
    }
  }, [id, api]);

  // Debounced pool count
  const updateCount = useCallback(async () => {
    setCountLoading(true);
    setCountError(false);
    try {
      const hardFilters = { seniority_levels: criteria.seniority_levels, functional_areas: criteria.functional_areas, therapeutic_areas: criteria.therapeutic_areas, degrees: criteria.degrees, geography_regions: criteria.geography_regions, employer_categories: criteria.employer_categories, remote_willing: criteria.remote_willing || null };
      const [countRes, statsRes] = await Promise.all([
        api.post("/candidates/count", hardFilters),
        api.post("/candidates/aggregate-stats", hardFilters)
      ]);
      setPoolCount(countRes.data.count);
      setStats(statsRes.data);
    } catch (e) { console.error("Count error:", e); setCountError(true); setPoolCount(null); }
    finally { setCountLoading(false); }
  }, [api, criteria.seniority_levels, criteria.functional_areas, criteria.therapeutic_areas, criteria.degrees, criteria.geography_regions, criteria.employer_categories, criteria.remote_willing]);

  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    countTimer.current = setTimeout(updateCount, 400);
    return () => clearTimeout(countTimer.current);
  }, [updateCount]);

  const updateCriteria = (key, val) => {
    setCriteria(prev => ({ ...prev, [key]: val }));
    // If criteria change after Deep Analysis was run, mark as stale
    if (analysisState === "done") {
      setCriteriaChanged(true);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Please enter a role title"); return; }
    setSaving(true);
    try {
      if (searchId) {
        await api.put(`/role-searches/${searchId}`, { title, type: searchType, criteria, role_description_text: jdText });
        // If criteria changed since last analysis, clear stale results
        if (criteriaChanged) {
          setResults([]);
          setSelectedCandidates([]);
          setAnalysisState("idle");
          setCriteriaChanged(false);
          setMinScore(0);
          toast.success("Search updated — run Deep Analysis to score the new pool");
        } else {
          toast.success("Search updated");
        }
      } else {
        const res = await api.post("/role-searches", { title, type: searchType, criteria, role_description_text: jdText });
        setSearchId(res.data.id);
        navigate(`/search/${res.data.id}`, { replace: true });
        toast.success("Search saved");
      }
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleDeepAnalysis = async () => {
    if (!searchId) { await handleSave(); }
    const sid = searchId;
    if (!sid) return;
    setAnalysisState("loading");
    setAnalysisProgress(0);
    try {
      const res = await api.post(`/deep-analysis/${sid}`);
      const taskId = res.data.task_id;
      const poll = setInterval(async () => {
        try {
          const status = await api.get(`/deep-analysis/${sid}/status?task_id=${taskId}`);
          setAnalysisProgress(status.data.progress || 0);
          setAnalysisMessage(status.data.message || "Processing...");
          if (status.data.status === "completed") {
            clearInterval(poll);
            setResults(status.data.results || []);
            setAnalysisState("done");
            setCriteriaChanged(false);
            toast.success("Deep Analysis complete");
          } else if (status.data.status === "error") {
            clearInterval(poll);
            setAnalysisState("idle");
            toast.error("Analysis failed");
          }
        } catch { clearInterval(poll); setAnalysisState("idle"); }
      }, 1000);
    } catch (e) {
      setAnalysisState("idle");
      if (e.response?.status === 429) {
        toast.error(e.response.data?.detail || "Daily analysis limit reached. Please contact the Varyynt team to expand capacity.");
      } else {
        toast.error("Failed to start analysis");
      }
    }
  };

  const handleJDParse = async () => {
    if (!jdText.trim()) return;
    setJdParsing(true);
    try {
      const res = await api.post("/jd-parse", { text: jdText });
      const d = res.data;
      setCriteria(prev => ({
        ...prev,
        seniority_levels: d.seniority_level ? [d.seniority_level] : prev.seniority_levels,
        functional_areas: d.functional_area ? [d.functional_area] : prev.functional_areas,
        therapeutic_areas: d.therapeutic_areas?.length ? d.therapeutic_areas : prev.therapeutic_areas,
        degrees: d.degrees?.length ? d.degrees : prev.degrees,
        geography_regions: d.geography_regions?.length ? d.geography_regions : prev.geography_regions,
        employer_categories: d.employer_categories?.length ? d.employer_categories : prev.employer_categories,
        role_scope_notes: d.role_scope_notes || prev.role_scope_notes,
      }));
      toast.success("JD parsed - criteria auto-filled");
    } catch { toast.error("JD parsing failed"); }
    finally { setJdParsing(false); }
  };

  const toggleCandidate = (anonId) => setSelectedCandidates(prev => prev.includes(anonId) ? prev.filter(id => id !== anonId) : [...prev, anonId]);

  const filteredResults = results.filter(r => r.composite_score >= minScore);

  const selectAll = () => setSelectedCandidates(filteredResults.map(r => r.candidate_anonymous_id));
  const deselectAll = () => setSelectedCandidates([]);
  const selectAboveThreshold = () => setSelectedCandidates(filteredResults.filter(r => r.composite_score >= minScore).map(r => r.candidate_anonymous_id));
  const allSelected = filteredResults.length > 0 && filteredResults.every(r => selectedCandidates.includes(r.candidate_anonymous_id));

  return (
    <div className="flex h-full" data-testid="talent-explorer">
      {/* LEFT PANEL - Criteria */}
      <div className="w-96 bg-white border-r border-slate-200 overflow-y-auto flex-shrink-0 p-6" data-testid="criteria-panel">
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Role title (e.g. VP Medical Affairs)"
          data-testid="role-title-input"
          className="w-full bg-transparent border-0 border-b-2 border-slate-200 px-0 py-4 text-xl font-heading text-slate-900 focus:border-evergreen-950 focus:ring-0 focus:outline-none rounded-none transition-colors placeholder:text-slate-300" />

        <div className="flex gap-2 mt-4 mb-6">
          {[["active_recruit", "Active Recruit"], ["succession_planning", "Succession"]].map(([val, label]) => (
            <button key={val} onClick={() => setSearchType(val)} data-testid={`type-${val}`}
              className={`px-4 py-1.5 text-sm font-accent rounded-full border transition-colors ${searchType === val ? "bg-evergreen-950 text-white border-evergreen-950" : "bg-white text-slate-500 border-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>

        <CollapsibleSection title="Job Description" defaultOpen={false} tip="Paste a job description and our AI will extract seniority, function, therapeutic area, geography, and degree criteria automatically.">
          <Textarea data-testid="jd-textarea" value={jdText} onChange={e => setJdText(e.target.value)} placeholder="Paste job description here..." className="min-h-[100px] text-sm" />
          <Button data-testid="parse-jd-btn" onClick={handleJDParse} disabled={jdParsing || !jdText.trim()} className="mt-2 bg-evergreen-950 text-white text-sm w-full" size="sm">
            <Sparkles className="w-3 h-3 mr-1" /> {jdParsing ? "Parsing..." : "Parse with AI"}
          </Button>
        </CollapsibleSection>

        <CollapsibleSection title="Seniority Level" tip="Filter by the candidate's inferred seniority based on their current job title. ~5% of candidates have ambiguous titles and will be excluded when this filter is active.">
          <ChipGroup options={SENIORITY_OPTIONS} selected={criteria.seniority_levels} onChange={v => updateCriteria("seniority_levels", v)} testId="seniority-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Functional Area" tip="The candidate's primary professional function within pharma/biotech, inferred from their title and career history.">
          <ChipGroup options={FA_OPTIONS} selected={criteria.functional_areas} onChange={v => updateCriteria("functional_areas", v)} testId="fa-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Therapeutic Areas" tip="Disease areas the candidate has experience in. ~44% of candidates have unknown TA and are excluded when this filter is active. Leave unselected to see all.">
          <ChipGroup options={TA_OPTIONS} selected={criteria.therapeutic_areas} onChange={v => updateCriteria("therapeutic_areas", v)} testId="ta-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Minimum Degree (Optional)" defaultOpen={false} tip="Only 23% of candidates have degree data. When active, candidates with unknown degree are still included to avoid over-filtering.">
          <ChipGroup options={DEGREE_OPTIONS} selected={criteria.degrees} onChange={v => updateCriteria("degrees", v)} testId="degree-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Employer Category" defaultOpen={false} tip="The type of organization the candidate currently works at (e.g. Large Pharma, Small Biotech). The actual company name is never shown.">
          <ChipGroup options={EMPLOYER_OPTIONS} selected={criteria.employer_categories} onChange={v => updateCriteria("employer_categories", v)} testId="employer-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Geography" tip="The candidate's general US region. Select 'Remote/Flexible' to include candidates open to remote work.">
          <ChipGroup options={GEO_OPTIONS} selected={criteria.geography_regions} onChange={v => updateCriteria("geography_regions", v)} testId="geo-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Remote Willing" defaultOpen={false} tip="Filter to only candidates who indicated willingness to work remotely.">
          <div className="flex items-center gap-3">
            <Switch data-testid="remote-switch" checked={criteria.remote_willing} onCheckedChange={v => updateCriteria("remote_willing", v)} />
            <span className="text-sm text-slate-600">Only show remote-willing candidates</span>
          </div>
        </CollapsibleSection>

        <Separator className="my-4" />
        <p className="text-xs text-slate-400 italic mb-4">Intent filters refine AI scoring - they do not change the candidate count above</p>

        <CollapsibleSection title="Desired Company Type (Intent)" defaultOpen={false} tip="What kind of company would this candidate find appealing? Used by Deep Analysis AI scoring only.">
          <ChipGroup options={COMPANY_TYPE_OPTIONS} selected={criteria.intent_company_types} onChange={v => updateCriteria("intent_company_types", v)} testId="intent-company-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Work Environment (Intent)" defaultOpen={false} tip="The candidate's preferred work setup. Fed into AI scoring to match against their stated preferences.">
          <ChipGroup options={WORK_ENV_OPTIONS} selected={criteria.intent_work_environments} onChange={v => updateCriteria("intent_work_environments", v)} testId="intent-env-chips" />
        </CollapsibleSection>
        <CollapsibleSection title="Role Scope Notes (Intent)" defaultOpen={false} tip="Free-text description of the ideal candidate's motivations. The more context you give, the more accurate the intent scoring.">
          <Textarea data-testid="scope-notes" value={criteria.role_scope_notes} onChange={e => updateCriteria("role_scope_notes", e.target.value)} placeholder="Describe what an ideal candidate would be looking for in their next move..." className="min-h-[80px] text-sm" />
        </CollapsibleSection>

        <div className="flex gap-2 mt-6 sticky bottom-0 bg-white py-4 border-t border-slate-100">
          <Button data-testid="save-search-btn" onClick={handleSave} disabled={saving} variant="outline" className="flex-1">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Search"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCriteria({ seniority_levels: [], functional_areas: [], therapeutic_areas: [], degrees: [], geography_regions: [], employer_categories: [], remote_willing: false, intent_company_types: [], intent_work_environments: [], role_scope_notes: "" })}>
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* RIGHT PANEL - Results */}
      <div className="flex-1 overflow-y-auto p-6" data-testid="results-panel">
        {/* Pool Counter */}
        <Card className="rounded-xl border bg-white shadow-sm mb-6 animate-fadeIn" data-testid="pool-counter-card">
          <CardContent className="p-6 text-center">
            {countError ? (
              <div>
                <p className="text-sm text-slate-500 mb-2">Unable to load candidates. Please refresh.</p>
                <Button variant="outline" size="sm" onClick={updateCount}>Retry</Button>
              </div>
            ) : (
              <>
                <div className="animate-countUp" key={poolCount}>
                  <span className="text-6xl font-accent font-bold text-evergreen-950" data-testid="pool-count">{countLoading ? "..." : (poolCount ?? "—")}</span>
                </div>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-2 inline-flex items-center gap-1.5">
                  Candidates match your criteria
                  <InfoTip text="Live count from our candidate database. Updates instantly as you adjust filters." />
                </p>
                <p className="text-sm text-slate-400 mt-1">Updates as you adjust filters</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Aggregate Stats */}
        {stats && poolCount > 0 && analysisState === "idle" && (
          <div className="grid grid-cols-2 gap-4 mb-6 animate-slideUp">
            {[
              { title: "Seniority", data: stats.seniority_distribution },
              { title: "Functional Areas", data: stats.functional_area_distribution },
              { title: "Geography", data: stats.geography_distribution },
              { title: "Therapeutic Areas", data: stats.therapeutic_area_distribution },
            ].map(({ title, data }) => (
              <Card key={title} className="rounded-xl border">
                <CardContent className="p-4">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">{title}</h4>
                  <div className="space-y-1.5">
                    {Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-2"><div className="bg-evergreen-950 rounded-full h-2" style={{ width: `${Math.min(100, (val / (poolCount || 1)) * 100)}%` }} /></div>
                        <span className="text-xs text-slate-500 w-20 truncate">{key}</span>
                        <span className="text-xs font-mono text-slate-400 w-8 text-right">{val}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Criteria Changed Banner */}
        {criteriaChanged && analysisState === "done" && (
          <Card className="border-amber-200 bg-amber-50 rounded-xl mb-4 animate-slideDown" data-testid="criteria-changed-banner">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-accent font-medium text-amber-900">Criteria have changed since the last analysis</p>
                <p className="text-xs text-amber-700 mt-0.5">Save your search and run Deep Analysis again to score the updated candidate pool.</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="border-amber-300 text-amber-800 hover:bg-amber-100">
                  <Save className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save & Clear"}
                </Button>
                <Button size="sm" onClick={async () => { await handleSave(); setTimeout(handleDeepAnalysis, 500); }} className="bg-evergreen-950 hover:bg-evergreen-900 text-white">
                  <Sparkles className="w-3 h-3 mr-1" /> Save & Re-analyze
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deep Analysis CTA — show when idle and pool > 0 */}
        {analysisState === "idle" && poolCount > 0 && (
          <Card className="bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white rounded-xl mb-6 animate-slideUp" data-testid="deep-analysis-cta">
            <CardContent className="p-6">
              <h3 className="text-lg font-heading mb-2">Ready for Deep Analysis?</h3>
              <p className="text-sm text-slate-300 mb-4">Our AI scores every candidate on two dimensions: <strong>Experience Fit</strong> (do their credentials match?) and <strong>Intent Alignment</strong> (does their career trajectory point toward your role?). This intent scoring uses proprietary data that no recruiter or job board can access.</p>
              <Button data-testid="run-deep-analysis-btn" onClick={handleDeepAnalysis} className="bg-lime-600 hover:bg-lime-700 text-white font-accent font-semibold px-8 py-3 rounded-lg">
                <Sparkles className="w-4 h-4 mr-2" /> Run Deep Analysis
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {analysisState === "loading" && (
          <Card className="rounded-xl border mb-6 animate-fadeIn" data-testid="analysis-loading">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-lime-100 flex items-center justify-center flex-shrink-0 animate-pulse-glow">
                  <Sparkles className="w-6 h-6 text-lime-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-accent font-medium text-slate-900" data-testid="analysis-message">{analysisMessage || "Starting analysis..."}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{analysisProgress}% complete</p>
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2.5" data-testid="analysis-progress-bar">
                <div className="bg-lime-600 h-2.5 rounded-full transition-all duration-500 ease-out" style={{ width: `${analysisProgress}%` }} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {analysisState === "done" && results.length > 0 && (
          <div data-testid="analysis-results" className="relative">
            {/* Sticky Results Toolbar */}
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl p-4 mb-4 shadow-sm" data-testid="results-toolbar">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-heading text-slate-900">{filteredResults.length} <span className="text-slate-400 font-body text-sm">candidates</span></h3>
                  {selectedCandidates.length > 0 && (
                    <span className="text-sm font-accent font-semibold text-lime-700 bg-lime-50 px-2.5 py-0.5 rounded-full" data-testid="selected-count">{selectedCandidates.length} selected</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="select-all-btn"
                    onClick={allSelected ? deselectAll : selectAll}
                    className="text-xs font-accent font-medium text-slate-500 hover:text-slate-900 px-2.5 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                  >
                    {allSelected ? "Deselect All" : "Select All"}
                  </button>
                  {selectedCandidates.length > 0 && (
                    <button
                      data-testid="clear-selection-btn"
                      onClick={deselectAll}
                      className="text-xs font-accent text-slate-400 hover:text-slate-600 px-2 py-1.5 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {/* Score slider row */}
              <div className="flex items-center gap-3" data-testid="min-score-filter">
                <span className="text-xs font-mono uppercase tracking-widest text-slate-400 whitespace-nowrap flex items-center gap-1">
                Min Score <InfoTip text="Filter results by minimum composite score. Higher threshold = fewer but stronger matches. The composite is 60% Experience Fit + 40% Intent Alignment." />
              </span>
                <input type="range" data-testid="min-score-slider" min="0" max="100" step="5" value={minScore}
                  onChange={e => setMinScore(parseInt(e.target.value))}
                  className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-evergreen-950" />
                <span className="text-sm font-accent font-bold text-evergreen-950 w-8 text-center" data-testid="min-score-value">{minScore}</span>
                {minScore > 0 && (
                  <button
                    data-testid="select-above-threshold-btn"
                    onClick={selectAboveThreshold}
                    className="text-xs font-accent font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
                  >
                    Select all {"\u2265"} {minScore}
                  </button>
                )}
              </div>
            </div>

            {/* Candidate Cards */}
            {filteredResults.map((r, idx) => (
              <CandidateCard key={r.id || idx} result={r} selected={selectedCandidates.includes(r.candidate_anonymous_id)} onToggle={() => toggleCandidate(r.candidate_anonymous_id)} index={idx} />
            ))}
            {filteredResults.length === 0 && results.length > 0 && (
              <Card className="border-dashed border-slate-300 bg-slate-50/50">
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-slate-500">No candidates meet the minimum score of {minScore}. Try lowering the threshold.</p>
                </CardContent>
              </Card>
            )}

            {/* Floating Action Bar */}
            {selectedCandidates.length > 0 && (
              <div className="sticky bottom-0 z-20 mt-4 animate-slideUp" data-testid="floating-action-bar">
                <div className="bg-evergreen-950 text-white rounded-xl p-4 shadow-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-accent font-bold">{selectedCandidates.length}</span>
                    <span className="text-sm text-slate-300">candidate{selectedCandidates.length !== 1 ? "s" : ""} selected</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={deselectAll} className="text-sm text-slate-400 hover:text-white transition-colors px-3 py-1.5" data-testid="floating-clear-btn">
                      Clear
                    </button>
                    <button
                      data-testid="floating-create-wo-btn"
                      onClick={() => navigate(`/work-orders/new?search=${searchId}&candidates=${selectedCandidates.join(",")}`)}
                      className="bg-lime-600 hover:bg-lime-700 text-white font-accent font-semibold px-6 py-2.5 rounded-lg transition-colors click-press"
                    >
                      Create Work Order
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
